import React from "react";
import { useAuthMe } from "./useAuthMe";
import type { CloudflareApplication } from "../layouts/sidebar/types";
import { parseCloudflareApplicationsFromAuth } from "../layouts/sidebar/parseCloudflareApplications";

/** `useAuthMe` persists here on success — read for first paint before `/me` resolves (avoids embed flash on refresh). */
function readStoredCloudflareApplications(): unknown {
  try {
    const raw = localStorage.getItem("auth_user");
    if (!raw) return undefined;
    const u = JSON.parse(raw) as { cloudflare_applications?: unknown };
    return u.cloudflare_applications;
  } catch {
    return undefined;
  }
}

/**
 * Cloudflare sidebar/embed data from stored session profile (cloudflareApplications).
 * While that request is in flight, falls back to `localStorage` from the last session
 * so embed routes still resolve on hard refresh.
 */
export function useMergedCloudflareApplications(): CloudflareApplication[] {
  const { data: authUser, isPending } = useAuthMe();
  return React.useMemo(() => {
    const fromServer = authUser?.cloudflare_applications;
    const stored = readStoredCloudflareApplications();
    const raw =
      fromServer !== undefined && fromServer !== null
        ? fromServer
        : isPending
          ? stored
          : (fromServer ?? stored);
    return parseCloudflareApplicationsFromAuth(raw);
  }, [authUser, isPending]);
}
